// The GitHub access the player GRANTS, instead of a token they paste (CCT-330).
//
// ⚠️ NOTHING IS ASKED OF THEM HERE, AND THE SCREEN MUST SAY SO. The previous panel asked for a
// repository address and a personal access token — a secret of theirs, that we then had to keep.
// Here they install an app on the repository of their choice, read-only, and can remove it from
// GitHub whenever they like. A player who expects to paste a token needs to read that they will
// not have to.
//
// ⚠️ THE LINK IS SHOWN, NOT ONLY OPENED. `openURL` can be swallowed by a popup blocker without
// the slightest sign — the same lesson as the Azure deployment link. A visible, selectable URL is
// what turns "nothing happened" into "click here".
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { apiFetch } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { getThemeColors } from '../utils/themeStyles';

interface Etat { status: string; connectionId?: string; account?: string; repositorySelection?: string }

export default function GithubApproval(
  { missionId, templateRepo = '' }: { missionId: number; templateRepo?: string },
) {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();

  const [etat, setEtat] = useState<Etat | null>(null);
  const [lien, setLien] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');

  // ⚠️ `apiFetch` AND NOT `fetch`: an expired token renews itself silently instead of leaving the
  // panel blank. A blank panel reads as "nothing to do here", which is the opposite of the point.
  const lire = useCallback(async () => {
    try {
      setEtat(await apiFetch<Etat>('/api/github/connections', {
        token: getAccessToken(), auth: true, background: true,
      }));
    } catch { /* network: stay silent rather than claim the access is missing */ }
  }, []);

  useEffect(() => { void lire(); }, [lire]);

  const approuver = async () => {
    setOccupe(true); setErreur('');
    try {
      const r = await apiFetch<{ installUrl: string }>(
        `/api/github/connections?missionId=${missionId}`,
        { method: 'POST', token: getAccessToken(), auth: true });
      setLien(r.installUrl);
      void Linking.openURL(r.installUrl).catch(() => { /* the link stays visible below */ });
    } catch (e: any) {
      // The server names what is missing (an unconfigured controller, no controller referenced):
      // replacing that by a generic failure would hide the only actionable part.
      setErreur(e?.data?.message || e?.message || t('github.failed'));
    } finally { setOccupe(false); }
  };

  const oublier = async () => {
    if (!etat?.connectionId) return;
    setOccupe(true); setErreur('');
    try {
      await apiFetch(`/api/github/connections/${etat.connectionId}`, {
        method: 'DELETE', token: getAccessToken(), auth: true });
      setEtat({ status: 'NONE' }); setLien('');
    } catch (e: any) {
      setErreur(e?.message || t('github.failed'));
    } finally { setOccupe(false); }
  };

  if (etat?.status === 'READY') {
    return (
      <View style={{ gap: 10 }}>
        <View style={s.ligne}>
          <Ionicons name="checkmark-circle" size={18} color={c.success} />
          <Text style={[s.fort, { color: c.textPrimary }]}>
            {t('github.grantedFrom')} {etat.account || '—'}
          </Text>
        </View>
        <Text style={[s.aide, { color: c.textTertiary }]}>{t('github.forgetHelp')}</Text>
        <TouchableOpacity onPress={oublier} disabled={occupe}
          style={[s.btn, { borderColor: c.border }]}>
          <Text style={[s.btnText, { color: c.textPrimary }]}>{t('github.forget')}</Text>
        </TouchableOpacity>
        {!!erreur && <Text style={[s.erreur, { color: c.warning }]}>{erreur}</Text>}
      </View>
    );
  }

  return (
    <View style={{ gap: 10 }}>
      {/* ⚠️ STEP 1: THE PLAYER CREATES THEIR OWN REPOSITORY. Before they own one, "authorise"
          has nothing to apply to. The template gives them a personal copy — GitHub does it alone,
          with no invitation from the author, no `administration` right for us, and no GitHub
          username to collect. */}
      {!!templateRepo && (
        <View style={[s.etape, { borderColor: c.border }]}>
          <Text style={[s.fort, { color: c.textPrimary }]}>{t('github.templateTitle')}</Text>
          <Text style={[s.aide, { color: c.textTertiary }]}>{t('github.templateBody')}</Text>
          <TouchableOpacity onPress={() => { void Linking.openURL(templateRepo); }}
            style={[s.btn, { borderColor: c.primary }]}>
            <Text style={[s.btnText, { color: c.primary }]}>{t('github.templateOpen')}</Text>
          </TouchableOpacity>
        </View>
      )}

      <Text style={[s.fort, { color: c.textPrimary }]}>{t('github.approveTitle')}</Text>
      <Text style={[s.aide, { color: c.textTertiary }]}>{t('github.approveBody')}</Text>

      <TouchableOpacity onPress={approuver} disabled={occupe}
        style={[s.btnPlein, { backgroundColor: c.primary }]}>
        {occupe ? <ActivityIndicator color="#fff" />
          : <Text style={[s.btnText, { color: '#fff' }]}>{t('github.approve')}</Text>}
      </TouchableOpacity>

      {!!lien && (
        <View style={{ gap: 6 }}>
          <Text style={[s.aide, { color: c.textTertiary }]}>{t('github.openLink')}</Text>
          {/* Selectable: a blocked popup leaves this as the only way through. */}
          <Text selectable style={[s.lien, { color: c.primary, borderColor: c.border }]}>{lien}</Text>
          <Text style={[s.aide, { color: c.textTertiary }]}>{t('github.pending')}</Text>
          <TouchableOpacity onPress={lire} style={[s.btn, { borderColor: c.border }]}>
            <Text style={[s.btnText, { color: c.textPrimary }]}>{t('github.installed')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {!!erreur && <Text style={[s.erreur, { color: c.warning }]}>{erreur}</Text>}
    </View>
  );
}

const s = StyleSheet.create({
  etape: { borderWidth: 1, borderRadius: 10, padding: 12, gap: 8 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  fort: { fontSize: 14, fontWeight: '700' },
  aide: { fontSize: 12, lineHeight: 17 },
  lien: { fontSize: 11, borderWidth: 1, borderRadius: 8, padding: 8 },
  btn: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 9,
         alignItems: 'center' },
  btnPlein: { borderRadius: 8, paddingHorizontal: 12, paddingVertical: 11, alignItems: 'center' },
  btnText: { fontSize: 13, fontWeight: '600' },
  erreur: { fontSize: 12, lineHeight: 17 },
});
